import React, { useEffect, useMemo } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { alterarStatusSolicitaDebMunicipio } from '~/Services/Service';

const STATUS_FINALIZADO = 3;
const PREFIXO_DEBITOS = 'SOLICITAÇÃO DE DÉBITOS';

const CAMPOS_FINANCEIROS = [
  { name: 'vlr_iptu', label: 'IPTU', acrescimo: 'acrescimo_iptu' },
  { name: 'vlr_agua', label: 'Água', acrescimo: 'acrescimo_agua' },
  { name: 'vlr_condominio', label: 'Condomínio', acrescimo: 'acrescimo_condominio' },
  { name: 'vlr_tx_iluminacao', label: 'Taxa de Iluminação Pública', acrescimo: 'acrescimo_tx_iluminacao' },
  { name: 'vlr_multa_postura', label: 'Multa Postura', acrescimo: null },
  { name: 'vlr_limpeza_pm', label: 'Taxa Limpeza PM', acrescimo: null },
  { name: 'vlr_alvara_construcao', label: 'Alvará Construção', acrescimo: null },
];

const buildSchema = () =>
  Yup.object().shape({
    status_novo: Yup.number().nullable().required('Status obrigatório'), // V-15
    observacao: Yup.string().max(2000, 'Máximo 2000 caracteres'),
    valores: Yup.object().shape({
      validade: Yup.string().nullable(),
      vlr_iptu: Yup.number().min(0, 'Valor deve ser >= 0'),
      acrescimo_iptu: Yup.number().min(0).max(100, 'Acréscimo 0-100%'),
      vlr_agua: Yup.number().min(0),
      acrescimo_agua: Yup.number().min(0).max(100),
      vlr_condominio: Yup.number().min(0),
      acrescimo_condominio: Yup.number().min(0).max(100),
      vlr_tx_iluminacao: Yup.number().min(0),
      acrescimo_tx_iluminacao: Yup.number().min(0).max(100),
      vlr_multa_postura: Yup.number().min(0),
      vlr_limpeza_pm: Yup.number().min(0),
      vlr_alvara_construcao: Yup.number().min(0),
    }),
  });

const GrpValores = ({ formik }) => {
  // V-14: side-effect chkZerar
  useEffect(() => {
    if (formik.values.valores && formik.values.valores.zerar_valores) {
      const campos = [
        'vlr_iptu', 'acrescimo_iptu',
        'vlr_agua', 'acrescimo_agua',
        'vlr_condominio', 'acrescimo_condominio',
        'vlr_tx_iluminacao', 'acrescimo_tx_iluminacao',
        'vlr_multa_postura', 'vlr_limpeza_pm', 'vlr_alvara_construcao',
      ];
      campos.forEach((c) => formik.setFieldValue(`valores.${c}`, 0));
    }
  }, [formik.values.valores && formik.values.valores.zerar_valores]); // eslint-disable-line

  return (
    <div className="border rounded p-3 mb-3" style={{ backgroundColor: '#f8f9fa' }}>
      <strong>Valores Apurados</strong>
      <Row className="mt-2">
        {CAMPOS_FINANCEIROS.map((campo) => (
          <React.Fragment key={campo.name}>
            <Col md={campo.acrescimo ? 4 : 6} className="mb-2">
              <Form.Group className="mb-0">
                <Form.Label style={{ fontSize: '0.8rem' }}>{campo.label}</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  name={`valores.${campo.name}`}
                  value={formik.values.valores[campo.name]}
                  onChange={formik.handleChange}
                  disabled={formik.values.valores.zerar_valores}
                />
              </Form.Group>
            </Col>
            {campo.acrescimo && (
              <Col md={2} className="mb-2">
                <Form.Group className="mb-0">
                  <Form.Label style={{ fontSize: '0.8rem' }}>Acréscimo (%)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    name={`valores.${campo.acrescimo}`}
                    value={formik.values.valores[campo.acrescimo]}
                    onChange={formik.handleChange}
                    disabled={formik.values.valores.zerar_valores}
                  />
                </Form.Group>
              </Col>
            )}
          </React.Fragment>
        ))}
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group>
            <Form.Label className="font-weight-bold" style={{ fontSize: '0.85rem' }}>
              Validade da Cotação *
            </Form.Label>
            <Form.Control
              type="date"
              name="valores.validade"
              value={formik.values.valores.validade}
              onChange={formik.handleChange}
              min={new Date().toISOString().split('T')[0]}
            />
          </Form.Group>
        </Col>
        <Col md={6} className="d-flex align-items-center pt-3">
          <Form.Check
            type="checkbox"
            label="Zerar todos os valores"
            name="valores.zerar_valores"
            checked={formik.values.valores.zerar_valores}
            onChange={formik.handleChange}
            id="chk-zerar-valores"
          />
        </Col>
      </Row>
    </div>
  );
};

const AlterarStatusModal = ({
  show, onHide, onSuccess, solicitacoesSelecionadas, statusList, tiposSolicitacao,
}) => {
  const tiposSolicDebito = (tiposSolicitacao || [])
    .filter((t) => t.assunto && t.assunto.startsWith(PREFIXO_DEBITOS))
    .map((t) => t.value);

  // V-15: filtrar status que diferem do atual (só se houver 1 selecionado)
  const statusDisponiveis = useMemo(() => {
    if (!solicitacoesSelecionadas || solicitacoesSelecionadas.length === 0) return statusList;
    const statusAtual =
      solicitacoesSelecionadas[0] != null ? solicitacoesSelecionadas[0].status : undefined;
    return (statusList || []).filter((s) => s.value !== statusAtual);
  }, [solicitacoesSelecionadas, statusList]);

  const initialValues = {
    status_novo: null,
    observacao: '',
    valores: {
      vlr_iptu: 0,
      acrescimo_iptu: 0,
      vlr_agua: 0,
      acrescimo_agua: 0,
      vlr_condominio: 0,
      acrescimo_condominio: 0,
      vlr_tx_iluminacao: 0,
      acrescimo_tx_iluminacao: 0,
      vlr_multa_postura: 0,
      vlr_limpeza_pm: 0,
      vlr_alvara_construcao: 0,
      validade: '',
      zerar_valores: false,
    },
  };

  const handleSubmit = async (values, formikBag) => {
    const ehFinalizado = values.status_novo === STATUS_FINALIZADO;
    const todosSolicDebito =
      solicitacoesSelecionadas &&
      solicitacoesSelecionadas.every((s) => tiposSolicDebito.includes(s.codigo_tipo_solicitacao));

    if (ehFinalizado && todosSolicDebito && !values.valores.validade && !values.valores.zerar_valores) {
      toast.error('Informe a validade da cotação para finalizar uma Solicitação de Débitos.');
      formikBag.setSubmitting(false);
      return;
    }

    try {
      const codigos = (solicitacoesSelecionadas || []).map((s) => s.codigo);
      const payload = {
        codigos,
        status_novo: values.status_novo,
        observacao: values.observacao,
        valores: ehFinalizado && todosSolicDebito ? values.valores : null,
      };
      await alterarStatusSolicitaDebMunicipio(payload);
      toast.success('Status alterado com sucesso.');
      onSuccess();
    } catch (err) {
      toast.error(err.message);
    } finally {
      formikBag.setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton style={{ backgroundColor: '#0747a6', color: '#fff' }}>
        <Modal.Title style={{ fontSize: '1rem', color: '#fff' }}>
          Alterar Status das Solicitações Selecionadas
        </Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={initialValues}
        validationSchema={buildSchema()}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => {
          const ehFinalizado = formik.values.status_novo === STATUS_FINALIZADO;
          const todosSolicDebito =
            solicitacoesSelecionadas &&
            solicitacoesSelecionadas.every((s) =>
              tiposSolicDebito.includes(s.codigo_tipo_solicitacao)
            );
          const deveExibirGrpValores = ehFinalizado && todosSolicDebito;

          return (
            <Form onSubmit={formik.handleSubmit}>
              <Modal.Body>
                {solicitacoesSelecionadas && solicitacoesSelecionadas.length > 0 && (
                  <Alert variant="info" className="py-2 mb-3" style={{ fontSize: '0.85rem' }}>
                    {solicitacoesSelecionadas.length} solicitação(ões) selecionada(s):{' '}
                    {solicitacoesSelecionadas.map((s) => `#${s.codigo}`).join(', ')}
                  </Alert>
                )}

                <Form.Group>
                  <Form.Label className="font-weight-bold">Novo Status *</Form.Label>
                  <Select
                    placeholder="Selecione..."
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    options={statusDisponiveis}
                    value={statusDisponiveis.find((s) => s.value === formik.values.status_novo) || null}
                    onChange={(opt) => formik.setFieldValue('status_novo', opt ? opt.value : null)}
                  />
                  {formik.touched.status_novo && formik.errors.status_novo && (
                    <div className="text-danger" style={{ fontSize: '0.8rem' }}>
                      {formik.errors.status_novo}
                    </div>
                  )}
                </Form.Group>

                <Form.Group>
                  <Form.Label className="font-weight-bold">Observação</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="observacao"
                    value={formik.values.observacao}
                    onChange={formik.handleChange}
                    placeholder="Observações sobre a alteração de status..."
                  />
                </Form.Group>

                {/* V-10: grpValores só se FINALIZADO + tipo SOLIC. DÉBITOS */}
                {deveExibirGrpValores && <GrpValores formik={formik} />}
              </Modal.Body>

              <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                  Cancelar
                </Button>
                <Button variant="primary" type="submit" disabled={formik.isSubmitting}>
                  {formik.isSubmitting ? 'Salvando...' : 'Salvar'}
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default AlterarStatusModal;
